import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from 'react';
import { observeWithSharedObserver } from './scrollRevealObserverPool';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

interface ScrollRevealProps {
  children: ReactNode;
  delay?: number;
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
  className?: string;
  style?: CSSProperties;
}

function getReducedMotionPreference(): boolean {
  return typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

function normalizeDelayStep(delay: number): number {
  if (!Number.isFinite(delay)) return 0;
  return Math.min(6, Math.max(0, Math.round(delay)));
}

export function ScrollReveal({
  children,
  delay = 0,
  once = true,
  threshold = 0.2,
  rootMargin = '0px 0px -10% 0px',
  className,
  style,
}: ScrollRevealProps) {
  const elementRef = useRef<HTMLDivElement | null>(null);
  const [reducedMotion, setReducedMotion] = useState(() => getReducedMotionPreference());
  const [visible, setVisible] = useState(() => getReducedMotionPreference());

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const mediaQuery = window.matchMedia(REDUCED_MOTION_QUERY);
    const updatePreference = () => {
      const isReduced = mediaQuery.matches;
      setReducedMotion(isReduced);
      if (isReduced) {
        setVisible(true);
      }
    };

    updatePreference();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updatePreference);
      return () => mediaQuery.removeEventListener('change', updatePreference);
    }

    mediaQuery.addListener(updatePreference);
    return () => mediaQuery.removeListener(updatePreference);
  }, []);

  useEffect(() => {
    const target = elementRef.current;
    if (!target) return;

    if (reducedMotion) {
      setVisible(true);
      return;
    }

    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }

    setVisible(false);

    let stopObserving = () => {
      // noop
    };

    stopObserving = observeWithSharedObserver(
      target,
      { threshold, rootMargin },
      (entry) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (once) {
            stopObserving();
          }
          return;
        }

        if (!once) {
          setVisible(false);
        }
      }
    );

    return stopObserving;
  }, [once, reducedMotion, rootMargin, threshold]);

  const delayStep = normalizeDelayStep(delay);
  const classes = ['reveal', visible ? 'is-visible' : '', className ?? ''].filter(Boolean).join(' ');

  return (
    <div
      ref={elementRef}
      className={classes}
      data-delay={delayStep > 0 ? String(delayStep) : undefined}
      style={style}
    >
      {children}
    </div>
  );
}
